import { cleanQuery } from '@mi-front/core';
import { FilterDefinition, ListViewExportParams, ListViewQueryParams } from '../list-view/types';

/** Options for transforming list params to CRUD API (filter definitions + context for toApi). */
export interface TransformToCrudApiOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterDefinitions?: FilterDefinition<any, any>[];
  context?: Record<string, unknown>;
}

/** Sort in API format: either "id asc|desc" string or { id, desc } object. */
type SortInput = string | { id: string; desc: boolean } | undefined;

function toCrudSortArray(sort: SortInput): string[] | undefined {
  if (sort == null) return undefined;
  if (typeof sort === 'string') {
    const parts = sort.trim().split(/\s+/);
    return parts.length >= 2 ? [`${parts[0]},${parts[1].toUpperCase()}`] : undefined;
  }

  return [`${sort.id},${sort.desc ? 'DESC' : 'ASC'}`];
}

/**
 * Processes filters (standard and raw) into CRUD API format.
 * - Standard filters go into the filterArray (e.g. ['key||eq||val'])
 * - Raw filters go into the rawParams object (e.g. { key: 'val' })
 */
export function processFilters(
  filters: Record<string, unknown>,
  definitions?: FilterDefinition[],
  context?: Record<string, unknown>,
) {
  const filterArray: string[] = [];
  const rawParams: Record<string, string> = {};

  Object.entries(filters).forEach(([id, value]) => {
    const definition = definitions?.find((d) => d.name === id);

    // If definitions are provided, skip keys that have no definition (unless they are raw, handled below)
    // Note: If definitions are missing, we treat everything as a standard filter (default behavior)
    if (definitions?.length && !definition) {
      return;
    }

    let processed: string | string[] | null | undefined;

    if (definition?.toApi) {
      processed = definition.toApi(value, context);
    } else {
      // Default CRUD format
      processed = id.includes('||') ? `${id}||${value}` : `${id}||$eq||${value}`;
    }

    if (processed == null) return;

    if (definition?.raw) {
      // Raw params: take the value directly (or first element if array)
      const val = Array.isArray(processed) ? processed[0] : processed;
      if (val != null && val !== '') {
        rawParams[id] = val;
      }
    } else {
      // Standard filters: add to filter array
      const arr = Array.isArray(processed) ? processed : [processed];
      filterArray.push(...arr);
    }
  });

  return { filterArray, rawParams };
}

export function transformParamsToCrudApi(
  params: ListViewQueryParams,
  options?: TransformToCrudApiOptions,
) {
  const filters = cleanQuery(params.filters ?? {});
  const { filterArray, rawParams } = processFilters(
    filters,
    options?.filterDefinitions,
    options?.context,
  );

  return cleanQuery({
    page: params.pageIndex + 1,
    limit: params.pageSize,
    sort: params.sort ? toCrudSortArray(params.sort) : undefined,
    filter: filterArray,
    format: params.format,
    ...rawParams,
  });
}

/**
 * Transforms export params (no pagination) to CRUD API shape for export endpoints.
 * Delegates to transformParamsToCrudApi and omits page/limit.
 */
export function transformExportParamsToCrudApi(
  params: ListViewExportParams,
  options?: TransformToCrudApiOptions,
) {
  const result = transformParamsToCrudApi({ ...params, pageIndex: 0, pageSize: 0 }, options);
  const { page: _page, limit: _limit, ...rest } = result;
  return cleanQuery(rest);
}
